/**
 * Role Approvals — the three-way sign-off on a proposed role.
 *
 * A role proposed from the Org Planning Overview needs the Chief People Officer, the CEO and the
 * CFO to agree before it becomes part of the hiring plan. This page is where that happens, and
 * where anyone with org-planning access can see where a proposal has got to.
 *
 * ⚠️ The decision controls render for the three approvers and for People Space admins only — but
 * that is presentation. Every decision is re-authorized by the proxy against the same three people
 * (`proxy-server/lib/roleApprovals.js#decisionRefusal`), because anyone who can open this page can
 * call the route directly. Never move the check up here.
 *
 * Read access is `space.people.internal_playground.view`, shared with the Org Planning Overview:
 * proposed headcount is that page's data seen from a different angle, and the two are meant to have
 * one audience (the `org-planning-viewers` Workspace group).
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ClipboardCheck, RefreshCw } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { CockpitTabs } from './taCockpit/cockpitTabs';
import { ProposalCard } from './roleApprovals/ProposalCard';
import { ProposalGroupCard } from './roleApprovals/ProposalGroupCard';
import { groupProposals, submissionDecisionNotice } from './roleApprovals/proposalGroups';
import { ChangeRequestCard } from './roleApprovals/ChangeRequestCard';
import { RADIUS } from './roleApprovals/approvalUi';
import { BLUE_BUTTON, BUTTON_CONTENT } from './orgPlanningChrome';
import {
  applyChangeRequest,
  fetchRoleApprovals,
  recordApprovalDecision,
  recordChangeDecision,
  recordSubmissionDecision,
  retryHiringPlanTransfer,
  setProposalPriority,
  withdrawChangeRequest,
} from '../services/roleApprovalsApi';
import type {
  ApprovalDecision,
  ApproverKey,
  PriorityRating,
  RoleChangeRequest,
  RoleProposal,
} from '../types/roleApprovals';

const QUERY_KEY = ['role-approvals'];

type Bucket = 'open' | 'decided';

/**
 * Two buckets, and open is the default.
 *
 * The page's job is the decisions that have not been taken — an approver opening it should land on
 * their queue rather than scroll past a year of settled roles. Everything decided stays one click
 * away because "what happened to the role I proposed?" is the other question this page answers,
 * and a declined role that simply vanished would answer it worst of all.
 */
function bucketOf(proposal: RoleProposal): Bucket {
  return proposal.status === 'Proposed' ? 'open' : 'decided';
}

/** A bulk submission stays in the queue while ANY of its roles is open: splitting it across the two
 * tabs would put back exactly the "which one is this?" confusion the grouping removes. */
function entryBucketOf(proposals: RoleProposal[]): Bucket {
  return proposals.some((p) => bucketOf(p) === 'open') ? 'open' : 'decided';
}

/** A change request is open while someone still has to act on it: decide it, or finish writing it. */
function changeBucketOf(request: RoleChangeRequest): Bucket {
  return request.state === 'pending' || request.state === 'approved' ? 'open' : 'decided';
}

function RoleApprovalsContent() {
  const queryClient = useQueryClient();
  const [bucket, setBucket] = useState<Bucket>('open');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchRoleApprovals,
    // Short: an approval is a thing two people may act on within a minute of each other, and a
    // stale queue is how the second one decides against what they think they are looking at.
    staleTime: 30 * 1000,
  });

  const decide = useMutation({
    mutationFn: ({
      proposalId,
      approverKey,
      decision,
      reason,
      priorityRating,
    }: {
      proposalId: string;
      approverKey: ApproverKey;
      decision: ApprovalDecision;
      reason?: string;
      priorityRating?: PriorityRating | null;
    }) => recordApprovalDecision(proposalId, approverKey, decision, reason, priorityRating),
    onSuccess: (result) => {
      setActionError(null);
      // The transfer is reported separately from the decision on purpose: the decision landed
      // either way, and a failed copy is a follow-up rather than a reason to re-approve. Same for
      // `warning`, which means the verdict was recorded but the role's overall status was not
      // recomputed — saying nothing there would leave a card that quietly disagrees with itself.
      if (result.warning) {
        setActionError(result.warning);
      } else if (result.transfer && !result.transfer.ok) {
        setActionError(result.transfer.message || 'The role could not be added to the hiring plan.');
      } else if (result.priority?.failed && result.transfer?.hiringPlanRecordId) {
        // The card is locked once the role is in the hiring plan, so "Save priority" cannot help.
        setActionError(
          'Approved by all three and now in the hiring plan, but the priority rating could not be ' +
            'saved. Set "Approved Priority Rating" on the role in the Hiring Database.'
        );
      } else if (result.priority?.failed) {
        setActionError(
          'Your approval was recorded, but the priority rating could not be saved. Try "Save priority" ' +
            'on the card; if it fails again, the proposals table is not accepting that rating.'
        );
      } else if (result.transfer?.priorityDropped) {
        setActionError(
          'Approved by all three and now in the hiring plan, but without its priority rating — the ' +
            'Hiring Database refused it. Set "Approved Priority Rating" on the role there.'
        );
      } else if (result.priority?.notice && !result.priority.notice.notified) {
        setActionError(
          `Approved, and the priority was changed to ${result.priority.approved ?? 'none'} — but the ` +
            'Slack thread could not be told. Mention the change in #squad_personnel_expense_planning.'
        );
      } else if (result.transfer?.hiringPlanRecordId) {
        setNotice('Approved by all three — the role is now in the hiring plan as a Backlog role.');
      } else {
        setNotice(null);
      }
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: Error) => setActionError(err.message),
  });

  const decideAll = useMutation({
    mutationFn: (v: {
      submissionId: string;
      approverKey: ApproverKey;
      decision: Exclude<ApprovalDecision, null>;
      reason?: string;
      priorityRatings?: Record<string, PriorityRating | null>;
    }) => recordSubmissionDecision(v.submissionId, v.approverKey, v.decision, v.reason, v.priorityRatings),
    onSuccess: (result, v) => {
      // Per role, said as one sentence: a bulk click can land on some roles and not others, and
      // the page has to say which rather than report the whole thing as done or failed.
      const { notice: text, error } = submissionDecisionNotice(result, v.decision);
      setActionError(error);
      setNotice(error ? null : text);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: Error) => {
      setActionError(err.message);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const transfer = useMutation({
    mutationFn: (proposalId: string) => retryHiringPlanTransfer(proposalId),
    // The service throws for every outcome that is NOT "the role is now in the plan", including
    // the 200 that means the claim is still held — see `retryHiringPlanTransfer`.
    onSuccess: (result) => {
      if (result.priorityDropped) {
        setNotice(null);
        setActionError(
          'The role is now in the hiring plan, but without its priority rating — the Hiring Database ' +
            'refused it. Set "Approved Priority Rating" on the role there.'
        );
      } else {
        setActionError(null);
        setNotice('The role is now in the hiring plan.');
      }
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: Error) => {
      setNotice(null);
      setActionError(err.message);
      // Refetch anyway: the failure may still have changed what the card should say.
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const priority = useMutation({
    mutationFn: (v: { proposalId: string; rating: PriorityRating | null }) =>
      setProposalPriority(v.proposalId, v.rating),
    onSuccess: (result) => {
      // The rating is saved either way; a Slack failure is said out loud rather than left for the
      // channel never to hear about.
      if (result.notice && !result.notice.notified) {
        setNotice(null);
        setActionError(
          `The priority is now ${result.approved ?? 'cleared'}, but the Slack thread could not be told. ` +
            'Mention the change in #squad_personnel_expense_planning.'
        );
      } else {
        setActionError(null);
        setNotice(
          result.notice?.notified
            ? `Priority set to ${result.approved ?? 'none'} — posted in the request's Slack thread.`
            : `Priority set to ${result.approved ?? 'none'}.`
        );
      }
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: Error) => setActionError(err.message),
  });

  /** Change requests share the decision flow; the three calls below mirror `decide` above. */
  const changeDecide = useMutation({
    mutationFn: (v: { requestId: string; approverKey: ApproverKey; decision: ApprovalDecision; reason?: string }) =>
      recordChangeDecision(v.requestId, v.approverKey, v.decision, v.reason),
    onSuccess: (result) => {
      setActionError(null);
      if (result.apply?.state === 'applied') {
        setNotice('Approved by all three — the change is now in the Hiring Database.');
      } else if (result.apply && !result.apply.applied) {
        setActionError(result.apply.message || 'The change was approved but could not be applied.');
      } else {
        setNotice(null);
      }
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: Error) => setActionError(err.message),
  });
  const changeApply = useMutation({
    mutationFn: (requestId: string) => applyChangeRequest(requestId),
    onSuccess: () => {
      setActionError(null);
      setNotice('The change is now in the Hiring Database.');
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: Error) => {
      setActionError(err.message);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
  const changeWithdraw = useMutation({
    mutationFn: (requestId: string) => withdrawChangeRequest(requestId),
    onSuccess: () => {
      setActionError(null);
      setNotice('The change request was withdrawn. The role can be edited again.');
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err: Error) => setActionError(err.message),
  });

  // A bulk submission is ONE entry — one card, one count — however many roles it carries.
  const entries = useMemo(() => groupProposals(data?.proposals || []), [data]);

  const counts = useMemo(() => {
    const changes = data?.changeRequests || [];
    const count = (b: Bucket) =>
      entries.filter((e) => entryBucketOf(e.proposals) === b).length +
      changes.filter((r) => changeBucketOf(r) === b).length;
    return { open: count('open'), decided: count('decided') };
  }, [data, entries]);

  const shown = useMemo(
    () => entries.filter((e) => entryBucketOf(e.proposals) === bucket),
    [entries, bucket]
  );
  const shownChanges = useMemo(
    () => (data?.changeRequests || []).filter((r) => changeBucketOf(r) === bucket),
    [data, bucket]
  );

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" aria-label="Loading role approvals" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`mx-auto mt-6 flex max-w-md items-start gap-3 ${RADIUS} border border-red-200 bg-red-50 p-4`}>
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
        <div className="text-sm text-red-700">{(error as Error).message}</div>
      </div>
    );
  }

  const busy =
    decide.isPending ||
    decideAll.isPending ||
    transfer.isPending ||
    priority.isPending || changeDecide.isPending || changeApply.isPending || changeWithdraw.isPending;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* The cockpit's own segmented control, `soft` so its radius matches the buttons and cards
            beside it — the same bar, in the same slot, as the Org Planning Overview next door. It
            carries the tab semantics (roving focus, selected state) that a pair of hand-rolled
            chips would have had to reimplement. */}
        <CockpitTabs
          value={bucket}
          onChange={setBucket}
          ariaLabel="Which proposals to show"
          shape="soft"
          tabs={[
            { value: 'open', label: `Awaiting approval (${counts.open})` },
            { value: 'decided', label: `Decided (${counts.decided})` },
          ]}
        />
        <Button
          size="sm"
          variant="outline"
          className={BLUE_BUTTON}
          contentClassName={BUTTON_CONTENT}
          onClick={() => refetch()}
          // `disabled`, not the atom's `loading`: `loading` sets `pointer-events-none` and
          // `aria-busy` but never `disabled`, so it stops the mouse and not the keyboard.
          disabled={isFetching}
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Approving is an async action whose only feedback is these two boxes, so they are
          announced: without a role a screen-reader user presses Approve and hears nothing at all. */}
      {actionError && (
        <div role="alert" className={`${RADIUS} border border-red-200 bg-red-50 p-3 text-sm text-red-700`}>
          {actionError}
        </div>
      )}
      {notice && !actionError && (
        <div
          role="status"
          className={`${RADIUS} border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800`}
        >
          {notice}
        </div>
      )}

      {data.changeRequestsError && (
        <div className={`${RADIUS} border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900`}>
          {data.changeRequestsError}
        </div>
      )}

      {shownChanges.map((request) => (
        <ChangeRequestCard
          key={request.id}
          request={request}
          viewer={data.viewer}
          busy={busy}
          onDecide={(requestId, approverKey, decision, reason) =>
            changeDecide.mutate({ requestId, approverKey, decision, reason })
          }
          onApply={(requestId) => changeApply.mutate(requestId)}
          onWithdraw={(requestId) => changeWithdraw.mutate(requestId)}
        />
      ))}

      {shown.length === 0 && shownChanges.length === 0 ? (
        <p className={`${RADIUS} border border-zinc-200 bg-white p-6 text-center text-sm text-zinc-500`}>
          {bucket === 'open'
            ? 'No roles are waiting for approval.'
            : 'No roles have been decided yet.'}
        </p>
      ) : (
        shown.map((entry) => {
          const shared = {
            viewer: data.viewer,
            busy,
            onDecide: (
              proposalId: string,
              approverKey: ApproverKey,
              decision: ApprovalDecision,
              reason?: string,
              priorityRating?: PriorityRating | null
            ) => decide.mutate({ proposalId, approverKey, decision, reason, priorityRating }),
            onRetryTransfer: (proposalId: string) => transfer.mutate(proposalId),
            onSetPriority: (proposalId: string, rating: PriorityRating | null) =>
              priority.mutate({ proposalId, rating }),
            priorityOwner: data.priorityOwner || 'the CEO',
          };
          return entry.submissionId ? (
            <ProposalGroupCard
              key={entry.submissionId}
              submissionId={entry.submissionId}
              proposals={entry.proposals}
              onDecideAll={(submissionId, approverKey, decision, reason, priorityRatings) =>
                decideAll.mutate({ submissionId, approverKey, decision, reason, priorityRatings })
              }
              {...shared}
            />
          ) : (
            <ProposalCard key={entry.proposals[0].id} proposal={entry.proposals[0]} {...shared} />
          );
        })
      )}

      <p className="text-xs text-zinc-400">
        A role is part of the hiring plan once all three have approved it — at that point it is
        copied into the hiring database as a <strong>Backlog</strong> role and counted as committed
        capacity. A change to a planned role that raises its cost by more than 10% is decided the
        same way, and only written to the hiring database once all three approve. A decline can be
        withdrawn, which returns it to awaiting approval. Roles proposed together are shown as one
        request: they can be approved all at once or one by one. A ✅ on the Slack announcement is not
        an approval — only a decision recorded here counts. The proposer suggests a priority; the CEO
        approves it with his approval of the role, or changes it — and a change is posted in the
        request&apos;s Slack thread.
      </p>
    </div>
  );
}

export default function RoleApprovalsView() {
  return (
    <div className="mx-auto max-w-[1000px] px-4 py-8">
      <header className="mb-6 flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-zinc-900">
          <ClipboardCheck className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">Role Approvals</h1>
          <p className="text-sm text-zinc-500">
            Proposed roles, and changes to planned roles, and their sign-off by the CPO, the CEO and the CFO
          </p>
        </div>
      </header>
      <RoleApprovalsContent />
    </div>
  );
}
